import React, { useMemo, useState } from "react";
import { updateTournamentTeams } from "services/tournamentService";
import { updateTeamInMatches } from "services/matchService";
import TeamEditModal from "./TeamEditModal";
import * as Sentry from "@sentry/react";

const TeamsTables = ({ teams, tournament, locationRankings = [], registrations = [], onTeamsUpdate }) => {
  const [editingTeam, setEditingTeam] = useState(null);
  const [editingDivision, setEditingDivision] = useState(null);
  const [isAddingTeam, setIsAddingTeam] = useState(false);

  const allTeams = useMemo(() =>
    [...(teams?.division1 || []), ...(teams?.division2 || [])],
    [teams]
  );

  const isSingles = useMemo(() =>
    allTeams.every((team) => team.players?.length === 1),
    [allTeams]
  );

  // Get all available players from registrations with rankings
  const availablePlayers = useMemo(() => {
    const playerMap = new Map();
    const teamMateNameMap = new Map();
    const allIds = new Set();

    // Build maps for user data from registrations
    registrations.forEach((reg) => {
      allIds.add(reg.userId);
      playerMap.set(reg.userId, reg);

      // Include teammates for doubles tournaments
      if (reg.teamMateId) {
        allIds.add(reg.teamMateId);
        if (reg.teamMateName) {
          teamMateNameMap.set(reg.teamMateId, reg.teamMateName);
        }
      }
    });

    // Create enriched user objects with rankings
    const enrichedUsers = Array.from(allIds).map((userId) => {
      const base = playerMap.get(userId);
      const rankingObj = locationRankings.find((lr) => lr.userId === userId);

      return {
        userId,
        name: base?.name || teamMateNameMap.get(userId) || userId,
        ranking: rankingObj?.ranking ?? 0,
        ...(base || {}),
      };
    });

    return enrichedUsers.sort((a, b) => (b.ranking || 0) - (a.ranking || 0));
  }, [registrations, locationRankings]);

  if (!teams || (!teams.division1?.length && !teams.division2?.length)) {
    return null;
  }

  const handleEditTeam = (team, divisionKey) => {
    setEditingTeam(team);
    setEditingDivision(divisionKey);
  };

  const handleSaveTeam = async (divisionKey, updatedTeam) => {
    try {
      const updatedTeams = { ...teams };
      const divisionTeams = updatedTeams[divisionKey];

      if (isAddingTeam) {
        // Adding a new team
        const newTeamNumber = Math.max(...divisionTeams.map(t => t.teamNumber), 0) + 1;
        const newTeam = {
          ...updatedTeam,
          teamNumber: newTeamNumber,
          division: divisionKey,
        };
        divisionTeams.push(newTeam);
      } else {
        // Editing existing team
        const teamIndex = divisionTeams.findIndex(
          (t) => t.teamNumber === updatedTeam.teamNumber
        );

        if (teamIndex === -1) {
          throw new Error("Équipe introuvable");
        }

        divisionTeams[teamIndex] = updatedTeam;

        // Update the team in all matches where it appears
        await updateTeamInMatches(
          tournament.id,
          divisionKey,
          updatedTeam.teamNumber,
          updatedTeam
        );
      }

      await updateTournamentTeams(tournament.id, updatedTeams);

      // Call the callback to update parent component if provided
      if (onTeamsUpdate) {
        onTeamsUpdate(updatedTeams);
      }

      setEditingTeam(null);
      setEditingDivision(null);
      setIsAddingTeam(false);

      // Reload page to reflect changes
      window.location.reload();
    } catch (error) {
      Sentry.captureException(error);
      console.error("Erreur lors de la mise à jour de l'équipe:", error);
      alert("Impossible d'enregistrer les modifications.");
    }
  };

  const handleDeleteTeam = async (divisionKey, teamNumber) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'équipe ${teamNumber} ?`)) {
      return;
    }

    try {
      const updatedTeams = { ...teams };
      const divisionTeams = updatedTeams[divisionKey];

      // Remove the team
      const filteredTeams = divisionTeams.filter(
        (t) => t.teamNumber !== teamNumber
      );

      // Renumber teams
      const renumberedTeams = filteredTeams.map((team, index) => ({
        ...team,
        teamNumber: index + 1,
      }));

      updatedTeams[divisionKey] = renumberedTeams;

      await updateTournamentTeams(tournament.id, updatedTeams);

      // Call the callback to update parent component if provided
      if (onTeamsUpdate) {
        onTeamsUpdate(updatedTeams);
      }

      // Reload page to reflect changes
      window.location.reload();
    } catch (error) {
      Sentry.captureException(error);
      console.error("Erreur lors de la suppression de l'équipe:", error);
      alert("Impossible de supprimer l'équipe.");
    }
  };

  const handleAddTeam = (divisionKey) => {
    setEditingTeam({
      teamNumber: 0, // Temporary, will be assigned on save
      players: [],
      totalRanking: 0,
    });
    setEditingDivision(divisionKey);
    setIsAddingTeam(true);
  };

  return (
    <div className="mt-6 w-full">
      {["division1", "division2"].map((divisionKey) => {
        const divisionTeams = teams[divisionKey];
        if (!divisionTeams?.length) return null;

        return (
          <div key={divisionKey} className="mb-4">
            <h3 className="text-sm font-bold mb-1">
              {!tournament?.makeDivisions
                ? (divisionKey === "division1" ? "Groupe 1" : "Groupe 2")
                : (divisionKey === "division1" ? "Division 1" : "Division 2")
              }
            </h3>
            <table className="w-full text-xs lg:text-sm bg-slate-800 text-slate-50 rounded overflow-hidden">
              <thead>
                <tr className="text-amber-400">
                  <th className="px-1 py-1 text-center">#</th>
                  <th className="px-1 py-1 text-left">Équipe</th>
                  <th className="px-1 py-1 text-center">Elo</th>
                  <th className="px-1 py-1 text-center"></th>
                </tr>
              </thead>
              <tbody>
                {divisionTeams.map((team) => {
                  const [p1, p2] = team.players ?? [];
                  const totalRanking =
                    (p1?.ranking ?? 0) + (p2?.ranking ?? 0);

                  return (
                    <tr
                      key={`${divisionKey}-${team.teamNumber}`}
                      className="border-t border-slate-700"
                    >
                      <td className="px-1 py-0.5 text-center font-bold text-amber-400">
                        {team.teamNumber}
                      </td>
                      <td className="px-1 py-0.5 truncate max-w-[140px]">
                        {p1?.name ?? "-"}
                        {!isSingles && ` & ${p2?.name ?? "-"}`}
                      </td>
                      <td className="px-1 py-0.5 text-center font-bold text-emerald-400">
                        {totalRanking}
                      </td>
                      <td className="px-1 py-0.5 text-center">
                        <button
                          onClick={() => handleEditTeam(team, divisionKey)}
                          className="text-yellow-500 hover:text-yellow-400 text-base px-1"
                        >
                          ✎
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <button
              onClick={() => handleAddTeam(divisionKey)}
              className="mt-2 bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs font-semibold"
            >
              + Ajouter une équipe
            </button>
          </div>
        );
      })}

      {/* Team Edit Modal */}
      {editingTeam && (
        <TeamEditModal
          team={editingTeam}
          divisionKey={editingDivision}
          availablePlayers={availablePlayers}
          onCancel={() => {
            setEditingTeam(null);
            setEditingDivision(null);
            setIsAddingTeam(false);
          }}
          onSave={handleSaveTeam}
          onDelete={handleDeleteTeam}
          isSingles={isSingles}
          isAdding={isAddingTeam}
        />
      )}
    </div>
  );
};

export default TeamsTables;
